import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './RouteMap.css';
import type { ScoredRoute } from './RouteResults';

interface RouteMapProps {
  route: ScoredRoute;
  coverageBubbles?: CoverageBubble[];
  onClose: () => void;
}

interface CoverageBubble {
  lat: number;
  lon: number;
  radiusNm: number;
  controllerType: 'DEL' | 'GND' | 'TWR' | 'APP' | 'CTR';
  callsign: string;
}

// Convert nautical miles to meters for Leaflet circles
const nmToMeters = (nm: number): number => nm * 1852;

/**
 * Filter coverage bubbles to only show those relevant to the route
 * A bubble is relevant if it's within a reasonable distance of the route line
 */
function filterRelevantBubbles(
  route: ScoredRoute,
  allBubbles: CoverageBubble[]
): CoverageBubble[] {
  if (!allBubbles || allBubbles.length === 0) return [];

  const depLat = route.departureLat;
  const depLon = route.departureLon;
  const arrLat = route.arrivalLat;
  const arrLon = route.arrivalLon;

  // Calculate route bounding box with padding
  const minLat = Math.min(depLat, arrLat) - 2;
  const maxLat = Math.max(depLat, arrLat) + 2;
  const minLon = Math.min(depLon, arrLon) - 2;
  const maxLon = Math.max(depLon, arrLon) + 2;

  // Filter bubbles that are within the bounding box
  return allBubbles.filter(bubble => {
    return (
      bubble.lat >= minLat &&
      bubble.lat <= maxLat &&
      bubble.lon >= minLon &&
      bubble.lon <= maxLon
    );
  });
}

export default function RouteMap({ route, coverageBubbles = [], onClose }: RouteMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  // Filter bubbles to only show relevant ones
  const relevantBubbles = filterRelevantBubbles(route, coverageBubbles);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Get airport coordinates from route data
    const depCoords = { lat: route.departureLat, lon: route.departureLon };
    const arrCoords = { lat: route.arrivalLat, lon: route.arrivalLon };

    // Calculate center point and zoom level
    const centerLat = (depCoords.lat + arrCoords.lat) / 2;
    const centerLon = (depCoords.lon + arrCoords.lon) / 2;

    // Initialize map
    const map = L.map(mapRef.current).setView([centerLat, centerLon], 6);
    mapInstanceRef.current = map;

    // Add tile layer - using CartoDB for better reliability
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap contributors © CARTO',
      subdomains: 'abcd',
      maxZoom: 20,
    }).addTo(map);

    // Create custom icons
    const airportIcon = L.divIcon({
      className: 'airport-marker',
      html: '<div class="airport-marker-inner"></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    // Add departure airport marker
    L.marker([depCoords.lat, depCoords.lon], { icon: airportIcon })
      .addTo(map)
      .bindPopup(`<b>${route.departure}</b><br/>${route.departureAirportName || 'Departure'}`);

    // Add arrival airport marker
    L.marker([arrCoords.lat, arrCoords.lon], { icon: airportIcon })
      .addTo(map)
      .bindPopup(`<b>${route.arrival}</b><br/>${route.arrivalAirportName || 'Arrival'}`);

    // Draw route line
    const routeLine = L.polyline(
      [
        [depCoords.lat, depCoords.lon],
        [arrCoords.lat, arrCoords.lon],
      ],
      {
        color: route.coverageScore >= 80 ? '#4caf50' : route.coverageScore >= 50 ? '#ff9800' : '#f44336',
        weight: 3,
        opacity: 0.7,
      }
    ).addTo(map);

    // Add coverage bubbles if provided (use filtered bubbles)
    relevantBubbles.forEach((bubble) => {
      const color = 
        bubble.controllerType === 'CTR' ? '#2196f3' :
        bubble.controllerType === 'APP' ? '#9c27b0' :
        '#4caf50';

      L.circle([bubble.lat, bubble.lon], {
        radius: nmToMeters(bubble.radiusNm),
        color: color,
        fillColor: color,
        fillOpacity: 0.1,
        weight: 1,
      })
        .addTo(map)
        .bindPopup(`<b>${bubble.callsign}</b><br/>${bubble.controllerType} - ${bubble.radiusNm}nm`);
    });

    // Fit bounds to show entire route
    map.fitBounds(routeLine.getBounds(), { padding: [50, 50] });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [route, relevantBubbles]);

  return (
    <div className="route-map-overlay">
      <div className="route-map-container">
        <div className="route-map-header">
          <h3>
            {route.departure} → {route.arrival}
          </h3>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        <div ref={mapRef} className="route-map" />
        <div className="route-map-legend">
          <h4>Legend</h4>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#4caf50' }}></div>
              <span>Excellent Coverage (80+)</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#ff9800' }}></div>
              <span>Good Coverage (50-79)</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#f44336' }}></div>
              <span>Limited Coverage (&lt;50)</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#2196f3' }}></div>
              <span>CTR Coverage</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#9c27b0' }}></div>
              <span>APP Coverage</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#4caf50' }}></div>
              <span>TWR Coverage</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
